import { Button } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import type { RhythmSubMode } from './rhythm-trainer-params.js'
import styles from './rhythm-trainer-welcome.module.css'

export interface RhythmTrainerWelcomeProps {
  /** Pre-selected sub-mode from `?mode=` URL param, or `null` for no pre-selection. */
  preSelected: RhythmSubMode | null
  onStart: (subMode: RhythmSubMode) => void
}

/**
 * Rhythm Trainer welcome screen. Two cards (Meter ID, Subdivision ID); URL
 * param `?mode=<meter|subdivision>` pre-selects one. Pure presenter — the
 * host owns the actual session start.
 *
 * Spec: docs/superpowers/specs/2026-05-22-rhythm-trainer-v1-design.md §6.1
 */
export const RhythmTrainerWelcome: FC<RhythmTrainerWelcomeProps> = props => {
  const { preSelected, onStart } = props
  const [selected, setSelected] = useState<RhythmSubMode | null>(preSelected)
  const intro = useTheoryCopy('rhythm-trainer')
  return (
    <section className={styles.welcome} aria-label="Rhythm Trainer welcome">
      {intro?.body ? <p className={styles.intro}>{intro.body}</p> : null}
      <p className={styles.intro}>Pick a sub-mode below, then start a 10-question session.</p>
      <div className={styles.cardGrid}>
        <button
          type="button"
          className={styles.card}
          aria-pressed={selected === 'meter'}
          onClick={() => setSelected('meter')}
        >
          <h3 className={styles.cardTitle}>Meter ID</h3>
          <p className={styles.cardBody}>Hear a beat, identify the time signature.</p>
          <p className={styles.cardMeta}>5 meters · 10 questions</p>
        </button>
        <button
          type="button"
          className={styles.card}
          aria-pressed={selected === 'subdivision'}
          onClick={() => setSelected('subdivision')}
        >
          <h3 className={styles.cardTitle}>Subdivision ID</h3>
          <p className={styles.cardBody}>Hear a beat, identify how it&rsquo;s divided.</p>
          <p className={styles.cardMeta}>4 subdivisions · 10 questions</p>
        </button>
      </div>
      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={selected === null}
        onClick={() => {
          if (selected !== null) {
            onStart(selected)
          }
        }}
      >
        Start session
      </Button>
    </section>
  )
}
